import { motion, useTransform } from 'framer-motion';
import { Scene, useSpan } from '../motion';
import PlayingCard from '../components/PlayingCard';
import { SHOWDOWNS, PLAYERS } from '../demoData';

// Deal order runs column by column; each pair lands at its own slot of progress.
const ORDER = SHOWDOWNS.levels.flatMap((level, col) => level.hands.map((_, row) => ({ col, row })));
const DEAL_START = 0.1;
const DEAL_END = 0.72;
const slot = (col, row) => {
  const i = ORDER.findIndex(o => o.col === col && o.row === row);
  const step = (DEAL_END - DEAL_START) / ORDER.length;
  return [DEAL_START + i * step, DEAL_START + (i + 1.6) * step];
};

function Pair({ p, col, row, item }) {
  const [a, b] = slot(col, row);
  const t = useSpan(p, a, b);
  const tilt = ((row + col) % 3 - 1) * 7; // fixed per slot
  const y = useTransform(t, [0, 1], [-60, 0]);
  const rotate = useTransform(t, [0, 1], [tilt, 0]);
  const opacity = useTransform(t, [0, 0.35], [0, 1]);

  return (
    <motion.li style={{ y, rotate, opacity }} className="flex items-center gap-3">
      <span className="flex gap-1">
        {item.cards.map(c => <PlayingCard key={c} card={c} />)}
      </span>
      <span className="pn-caption leading-tight">
        #{item.hand}
        <br />
        <span className="pn-num text-pn-fg">${item.amount.toFixed(2)}</span>
      </span>
    </motion.li>
  );
}

export default function ShowdownWall() {
  const player = PLAYERS.find(x => x.name === SHOWDOWNS.player);

  return (
    <Scene id="showdowns" length={2.3} label="Showdown hands">
      {p => (
        <div className="mx-auto flex h-full max-w-[1480px] flex-col justify-center px-4 pb-6 pt-20 sm:px-6 tall:px-12 land:grid land:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] land:items-center land:gap-16">
          <div>
            <h2 className="pn-h2 max-w-[13ch]">See what they showed up with.</h2>
            <p className="pn-lede mt-4">
              Every hand a player turned over at showdown, grouped by how much they put in before the flop.
            </p>
          </div>

          <div className="mt-6 land:mt-0">
            <div className="pn-bezel">
              <div className="pn-plate p-[clamp(1rem,0.6rem+1.6*var(--u),2rem)]">
                <div className="flex items-baseline justify-between">
                  <p className="pn-h3">{player.name}</p>
                  <p className="pn-caption">
                    VPIP {player.vpip} · 3-bet {player.threeBet} · example data
                  </p>
                </div>
                <div className="mt-[clamp(1rem,0.5rem+1.8*var(--u),2.25rem)] grid grid-cols-1 gap-6 sm:grid-cols-3">
                  {SHOWDOWNS.levels.map((level, col) => (
                    <div key={level.label} className="sm:border-l sm:border-pn-line/10 sm:pl-5 first:sm:border-l-0 first:sm:pl-0">
                      <p className="pn-caption mb-3">{level.label}</p>
                      <ul className="flex flex-wrap gap-x-5 gap-y-3 sm:flex-col">
                        {level.hands.map((item, row) => (
                          <Pair key={item.hand} p={p} col={col} row={row} item={item} />
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Scene>
  );
}
